"use client";
import { Link, usePathname } from "../../../navigation";
import useCMSPageByRoute from "../../cms/hooks/server/useCMSPageByRoute";
import usePageLinks from "../../cms/hooks/server/usePageLinks";
import * as Ariakit from "@ariakit/react";
import useIsMobile from "../../common/utils/useIsMobile";
import { useLocale } from "next-intl";
import NavigationItem from "./NavigationItem";
import MobileBackButton from "../../common/components/MobileBackButton";
import { useEffect, useRef } from "react";

const MenuDropdown = ({
  title,
  icon = null,
  active,
  href,
  selected,
  setSelected,
}) => {
  const locale = useLocale();
  const { page } = useCMSPageByRoute({ route: href, locale });
  const { links } = usePageLinks({ pageId: page?._id });
  const isMobile = useIsMobile();

  useEffect(() => {
    if (selected) setSelected(null);
  }, [isMobile]);

  const children = links?.children || [];
  const menuItems = children?.map(({ _id, title, route }) => ({
    key: _id,
    href: {
      pathname: route,
    },
    children: title,
  }));

  const menu = Ariakit.useMenuStore({ placement: "bottom-end" });
  const menuRef = useRef(null);

  const handleMouseEnter = () => {
    // Only handle hover on non-touch devices
    if (!("ontouchstart" in window)) {
      menu.show();
    }
  };

  const handleMouseLeave = () => {
    // Only handle hover on non-touch devices
    if (!("ontouchstart" in window)) {
      menu.hide();
    }
  };

  const handleClick = (e) => {
    // Handle click for touch devices
    if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
      e.preventDefault();
      menu.toggle();
    }
  };

  // Close menu when clicking outside on touch devices
  const open = menu.useState("open");
  useEffect(() => {
    if (!("ontouchstart" in window)) return;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && open) {
        menu.hide();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [menu, open]);

  if (!selected && isMobile)
    return (
      <Link
        href={href}
        className={`mkt-top-nav-link ${active ? "active" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setSelected(href);
        }}
      >
        {title}
      </Link>
    );
  if (isMobile)
    return (
      <div className="mkt-sub-nav full-width sortiment">
        <div className="container">
          {selected && (
            <MobileBackButton
              onClick={() => {
                setSelected(null);
              }}
            />
          )}
          <Link href={href} className={`mkt-top-nav-link active d-block mt-3`}>
            {title}
          </Link>
          {selected && (
            <div className="ps-1 row flex-column-reverse flex-lg-row">
              <div className="col-lg-3 navigation-list-column navigation-list-column--sortiment">
                {children.map((data, index) => {
                  const { route, title, _id } = data;
                  return (
                    <div key={`${title}-${_id}-${index}`}>
                      <NavigationItem
                        className="navigation-list-item navigation-list-item--sortiment nav-hover d-inline-flex align-items-center gap-3"
                        link={route}
                        title={title}
                      />{" "}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  return (
    <Ariakit.MenuProvider store={menu}>
      <div
        ref={menuRef}
        className="position-relative d-inline-block"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Ariakit.MenuButton
          className={`mkt-top-nav-link ${active ? "active" : ""}`}
          onClick={handleClick}
        >
          <Link
            href={href}
            onClick={(e) => {
              // Prevent navigation on touch devices when clicking to open menu
              if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
                e.preventDefault();
              }
            }}
          >
            {page?.title || title || icon}
          </Link>
        </Ariakit.MenuButton>

        <Ariakit.Menu gutter={8} className="publicare-menu">
          {menuItems
            .filter(Boolean)
            .map(({ key, children, href, onClick, active, disabled }) => (
              <Ariakit.MenuItem
                key={key}
                className="publicare-menu-item"
                aria-selected={active}
                tabIndex={0}
                disabled={disabled}
                onClick={(e) => {
                  onClick?.(e);
                  menu.hide();
                }}
              >
                {href ? <Link href={href}>{children}</Link> : children}
              </Ariakit.MenuItem>
            ))}
        </Ariakit.Menu>
      </div>
    </Ariakit.MenuProvider>
  );
};

export default function TopNavigationLink({
  showSeparator,
  href,
  children,
  showChildren,
  title,
  onSelected,
  selected,
  ...rest
}: {
  showSeparator?: boolean;
  showChildren?: boolean;
  selected?: null | string;
  id?: string;
  href: string;
  onSelected?: any;
  children: React.ReactNode;
} & React.ComponentProps<typeof Link>) {
  const pathname = usePathname();
  const isActive = pathname.includes(href);

  const Separator = showSeparator ? (
    <span className="separator" aria-hidden="true" role="presentation" />
  ) : null;

  if (showChildren)
    return (
      <>
        <MenuDropdown
          active={isActive}
          title={children}
          href={href}
          setSelected={onSelected}
          selected={selected}
        />
        {Separator}
      </>
    );

  return (
    <>
      <Link
        {...rest}
        href={href}
        className={`mkt-top-nav-link ${isActive ? "active" : ""}`}
      >
        {children}
      </Link>
      {Separator}
    </>
  );
}
